
import Vue from 'vue';
import {
	db,
	FieldValue,
	DocumentReference,
	DocumentSnapshot,
	Query,
	functions,
} from '@/firebase';
import { mapActions, mapState } from 'vuex';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import _keys from 'lodash/keys';
import _trim from 'lodash/trim';
import _union from 'lodash/union';
import _unionBy from 'lodash/unionBy';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment-timezone';
import {
	FieldDefUuid,
	RunnitNodeFieldLogic,
	RunnitNodeDef,
	RunnitNodeDefFieldMapBuilderKey,
	RunnitNodeField,
	RunnitNodeFieldGroup,
	RunnitNodeStaticFields,
	RunnitPrivateNodeDef,
	ProviderMapping,
	RunnitPrivateNodeDefFieldMapBuilders,
	RunnitPrivateNodeDefFieldMappings,
	hundredthsToTokens,
	tokensToHundredths,
	RunnitPrivateNodeDefFieldValues,
	Company,
	Avatar,
	RunnitPrivateFieldValueLogic,
	RunnitNodeDefTag,
	RunnitNodeDefTagType,
	RunnitNodeDefTagConnection,
	AVATAR_USE_CASE,
	IMAGE_UPLOAD_MODE,
	RUNNIT_NODE_DEF_FIELD_MAP_BUILDER_VALUE_TYPE,
	RUNNIT_NODE_DEF_PROVIDER,
	RUNNIT_NODE_DEF_TOOL_TYPE,
	RUNNIT_NODE_FIELD_TYPE,
	CostPerResultLogic,
	RUNNIT_NODE_DEF_TAG_TYPE,
	RunnitNodeDefToolType,
} from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	PROVIDER_ITEMS,
	NODE_TYPE_ITEMS,
	ENDPOINT_ID_ITEMS,
} from '@/views/Runnits/RunnitSettings/internalAdminOnly/constants';
import { get$bindFirestoreOptions } from '@/mixins';
import PurpleChip from '@/components/base/PurpleChip.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import NumberField from '@/components/base/NumberField.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import ClipboardCopy from '@/components/ClipboardCopy.vue';
import GreyButton from '@/components/base/GreyButton.vue';
import ImageUpload from '../ImageInput/ImageUpload.vue';
import RunnitImage from '../../RunnitImage.vue';
import { RUNNITS_OWNER_SELECTION } from '../../constants';
import ManageTagsDialog from './ManageTagsDialog.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BigNumber from 'bignumber.js';
import TokensSVG from '@/assets/TokensSVG.vue';
import { ROUTER } from '@/router/constants';
import FeaturedNodeDefResultsContainer from '@/views/Runnits/FeaturedNodeDefResultsContainer.vue';
import DatePicker from '@/components/base/DatePicker.vue';
import TimePicker from '@/components/base/TimePicker.vue';
import HtmlField from '@/components/HtmlField.vue';
import { combineDateAndTime, convertMomentToDateTimeInputValues } from '@/utils';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import RunnitProviderMappingRow from '@/views/Runnits/RunnitSettings/internalAdminOnly/RunnitProviderMappingRow.vue';


const stringifyMappingObject = (obj: unknown): string =>
	JSON.stringify(obj && typeof obj === 'object' ? obj : {}, null, 2);

type ParsedProviderMappingRow = {
	fieldMappings: RunnitPrivateNodeDefFieldMappings;
	fieldMapBuilders: RunnitPrivateNodeDefFieldMapBuilders;
};

type ProviderMappingJsonKind = 'fieldMappings' | 'fieldMapBuilders';

const createEmptyProviderMapping = (): ProviderMapping => ({
	provider: null,
	endpointId: null,
	version: '1.0.0',
	fieldMappings: {},
	fieldMapBuilders: {},
	staticFieldMappings: {},
} as ProviderMapping);

export default Vue.extend({
	name: 'RunnitDuplicateEditNodeDefInternalEditor',
	props: {
		value: { type: Boolean, default: false },
		maxWidth: { type: [Number, String], default: '1700px' },
		selectedNodeDef: { type: Object, default: null },
	},
	data () {
		const EDITOR_PAGES = {
			NODE_DEF: 'NODE_DEF',
			PRIVATE_NODE_DEF: 'PRIVATE_NODE_DEF',
		};
		const CAN_PICK_NUM_RESULTS = {
			N_A: null,
			YES: true,
			NO: false,
		};

		return {
			RUNNIT_NODE_DEF_TOOL_TYPE,
			EDITOR_PAGES,
			IMAGE_UPLOAD_MODE,
			AVATAR_USE_CASE,
			RUNNIT_NODE_DEF_TAG_TYPE,
			INTERNAL_PRICING_RUNNIT_TOKENS_MARGIN_MULTIPLE: 1.30,
			INTERNAL_PRICING_RUNNIT_TOKENS_CONVERSION_MULTIPLE: 0.0006,
			open: false,

			oldFields: null,
			newFields: null,

			privateOldFieldValues: null,
			privateNewFieldValues: null,
			oldProviderMappings: [] as ProviderMapping[],
			newProviderMappings: [] as ProviderMapping[],
			/** JSON text areas keyed by mapping index; kept separate from ProviderMapping objects */
			oldProviderFieldMappingsJson: [] as string[],
			newProviderFieldMappingsJson: [] as string[],
			oldProviderFieldMapBuildersJson: [] as string[],
			newProviderFieldMapBuildersJson: [] as string[],

			editorPage: EDITOR_PAGES.NODE_DEF,

			privateNodeDef: null,
			loadingPrivateNodeDef: false,

			savingRunnitNodeDef: false,

			formValid: false,

			id: null,
			title: null,
			description: null,
			articleUrl: null,
			publishedAt: null,
			publishedAtSelected: false,
			imageUrl: null,
			avatarId: null,
			avatar: null,

			sortOrder: 0,
			averageGenInSeconds: 0,
			tokensPerResult: 1000,
			oldCostPerResultLogics: null,
			newCostPerResultLogics: null,
			dollarsOurCostPerResult: 1,
			providerItems: PROVIDER_ITEMS,
			/** Which provider row is the source for generateToolJsons `currentFieldMappings` and AI fieldMappings updates */
			populateJsonTargetIndex: 0,
			tags: [],

			newUntil: null,
			showNewTag: false,

			NODE_TYPE_ITEMS,
			type: null,
			companies: [],
			fetchingCompanies: false,
			companyIdsArray: [],
			tagsDialogOpen: false,


			endpointIdsMap: {
				[RUNNIT_NODE_DEF_PROVIDER.RUNNIT_IMG]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.RUNNIT_IMG],
				[RUNNIT_NODE_DEF_PROVIDER.RD]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.RD],
				[RUNNIT_NODE_DEF_PROVIDER.OCTOAI]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.OCTOAI],
				[RUNNIT_NODE_DEF_PROVIDER.RUNPOD]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.RUNPOD],
				[RUNNIT_NODE_DEF_PROVIDER.IDEOGRAM]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.IDEOGRAM],
				[RUNNIT_NODE_DEF_PROVIDER.FALAI]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.FALAI],
				[RUNNIT_NODE_DEF_PROVIDER.NICHETENSOR]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.NICHETENSOR],
				[RUNNIT_NODE_DEF_PROVIDER.RUNWAY]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.RUNWAY],
				[RUNNIT_NODE_DEF_PROVIDER.LUMAAI]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.LUMAAI],
				[RUNNIT_NODE_DEF_PROVIDER.RUNWARE]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.RUNWARE],
				[RUNNIT_NODE_DEF_PROVIDER.MODAL]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.MODAL],
				[RUNNIT_NODE_DEF_PROVIDER.OPENAI]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.OPENAI],
				[RUNNIT_NODE_DEF_PROVIDER.TOPAZ]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.TOPAZ],
				[RUNNIT_NODE_DEF_PROVIDER.FREEPIK]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.FREEPIK],
				[RUNNIT_NODE_DEF_PROVIDER.VERTEX]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.VERTEX],
				[RUNNIT_NODE_DEF_PROVIDER.KIEAI]: ENDPOINT_ID_ITEMS[RUNNIT_NODE_DEF_PROVIDER.KIEAI],
			},
			coldStartWarningSeconds: null,

			CAN_PICK_NUM_RESULTS,
			canPickNumResults: CAN_PICK_NUM_RESULTS.YES,

			defaultNumResults: 1,
			minNumResults: 1,
			maxNumResults: 4,

			isUploadingAvatar: false,
			isReadOnly: true,
			showFeatured: false,
			previewFeaturedOpen: false,
			usePromptDrivenForm: false,
			batchable: false,
			onboardingDescriptionPreviewOnRight: 'RIGHT',
			onboardingData: {
				descriptionHtml: null,
				videoUrl: null,
				enabledAt: null,
			},
			onboardingEnabledAtDate: null,
			onboardingEnabledAtTimeInMinutes: null,
			populateJsonDialogOpen: false,
			populateJsonType: null,
			populateJsonText: null,
			populateJsonLoading: false,
			cmsData: {
				ghostPostIds: {},
			},
			ghostPostIdsArray: [],
			newGhostPostId: '',
			syncingCms: false,
		};
	},
	created () {
		this.fetchCompanies();
	},
	mounted () {
		// Add keyboard shortcut for Save button (Cmd+Enter)
		document.addEventListener('keydown', this.handleKeydown);
	},
	beforeDestroy () {
		// Clean up keyboard event listener
		document.removeEventListener('keydown', this.handleKeydown);
	},
	watch: {
		value: {
			immediate: true,
			handler (newVal: boolean) {
				this.open = !!newVal;
			},
		},
		selectedNodeDef: {
			immediate: true,
			async handler (newVal: RunnitNodeDef, oldVal: RunnitNodeDef) {
				if (newVal !== oldVal) {
					this.isReadOnly = true;
					this.id = newVal?.id || null;
					this.title = newVal?.title || null;
					this.description = newVal?.description || null;
					this.articleUrl = (newVal as any)?.articleUrl || null;
					this.imageUrl = newVal?.imageUrl || null; // deprecated
					this.avatarId = newVal?.avatarId || null;
					this.avatar = newVal?.avatar || null;
					this.sortOrder = newVal?.sortOrder || null;
					this.averageGenInSeconds = this._get(newVal, 'averageGenInSeconds') || 0;
					this.tokensPerResult = newVal?.costPerResult || 0;
					this.oldCostPerResultLogics = JSON.stringify(newVal?.costPerResultLogics || [], null, 2);
					this.newCostPerResultLogics = this.oldCostPerResultLogics;
					this.onTokensPerResultInput(hundredthsToTokens(this.tokensPerResult));
					this.coldStartWarningSeconds = newVal?.coldStartWarningSeconds || null;
					this.type = newVal?.type || null;
					this.companyIdsArray = _keys(newVal?.companyIds || []).filter((companyId: string) => newVal.companyIds[companyId]);
					this.publishedAt = newVal?.publishedAt
						? new Date(newVal.publishedAt.toMillis())
						: null;
					this.publishedAtSelected = !_isEmpty(newVal?.publishedAt);
					this.deletedAt = newVal?.deletedAt || null;

					this.newUntil = newVal?.newUntil
						? new Date(newVal.newUntil.toMillis()).toISOString()
						: null;
					this.showNewTag = !!this.newUntil;

					this.oldFields = JSON.stringify(newVal?.fields || {}, null, 2);
					this.newFields = this.oldFields;

					this.defaultNumResults = this._get(newVal, 'staticFields.numResults.defaultValue') || 1;
					this.minNumResults = this._get(newVal, 'staticFields.numResults.min') || 1;
					this.maxNumResults = this._get(newVal, 'staticFields.numResults.max') || 4;

					// Set onboarding data
					this.onboardingData = {
						descriptionHtml: newVal?.onboardingData?.descriptionHtml || null,
						videoUrl: newVal?.onboardingData?.videoUrl || null,
						enabledAt: newVal?.onboardingData?.enabledAt
							? new Date(newVal.onboardingData.enabledAt.toMillis())
							: null,
					};

					// Set CMS data
					this.cmsData = {
						ghostPostIds: newVal?.cmsData?.ghostPostIds || {},
					};
					this.ghostPostIdsArray = _keys(this.cmsData.ghostPostIds || {});

					this.tags = [];
					if (newVal?.tags) {
						try {
							await Promise.all((Object.keys(newVal?.tags) || []).map(async (tagId: string) => {
								const tagRef = db
									.doc(`runnitNodeDefTags/${tagId}`);
								const tag = {
									...(await tagRef.get()).data(),
									id: tagRef.id,
								} as RunnitNodeDefTag;
								if (!tag.isDeleted) {
									this.tags.push(tag);
								}
								return tag;
							}));
						} catch (e) {
							console.error(e);
							this.updateSnackbar({
								status: SNACKBAR_STATUS.ERROR,
								message: 'Error loading tool categories, please reach out to report issues by going to Resources -> Help from the menu options',
								show: true,
							});
						}
					}

					if (newVal) {
						await this.bindPrivateNodeDef(newVal);
					}
					this.showFeatured = newVal?.showFeatured || false;
					this.usePromptDrivenForm = newVal?.usePromptDrivenForm || false;
					this.batchable = !!newVal?.batchable
				}
			},
		},
		privateNodeDef: {
			immediate: true,
			async handler (newVal: RunnitPrivateNodeDef, oldVal: RunnitPrivateNodeDef) {
				if (newVal !== oldVal) {
					const incoming: ProviderMapping[] = (newVal?.providerMappings || []).map((m) => _cloneDeep(m));
					const list: ProviderMapping[] = incoming.length ? incoming : [createEmptyProviderMapping()];
					this.oldProviderMappings = list.map((m) => _cloneDeep(m));
					this.newProviderMappings = list.map((m) => _cloneDeep(m));
					this.oldProviderFieldMappingsJson = list.map((m) => stringifyMappingObject(m.fieldMappings));
					this.newProviderFieldMappingsJson = this.oldProviderFieldMappingsJson.slice();
					this.oldProviderFieldMapBuildersJson = list.map((m) => stringifyMappingObject(m.fieldMapBuilders));
					this.newProviderFieldMapBuildersJson = this.oldProviderFieldMapBuildersJson.slice();
					this.populateJsonTargetIndex = Math.min(this.populateJsonTargetIndex || 0, this.newProviderMappings.length - 1);
					const primaryMapping: ProviderMapping = this.getActiveProviderMapping(this.newProviderMappings, 0);

					this.canPickNumResults = this._get(primaryMapping, 'staticFieldMappings.numResults')
						? (this._get(this.selectedNodeDef.staticFields, 'numResults') ? this.CAN_PICK_NUM_RESULTS.YES : this.CAN_PICK_NUM_RESULTS.NO)
						: this.CAN_PICK_NUM_RESULTS.N_A;
					this.defaultNumResults = this.canPickNumResults === this.CAN_PICK_NUM_RESULTS.NO
						? (this._get(newVal, 'staticFieldValues.numResults') || 1)
						: this.defaultNumResults;

					this.privateOldFieldValues = JSON.stringify(newVal?.fieldValues || {}, null, 2);
					this.privateNewFieldValues = this.privateOldFieldValues;
				}
			},
		},
		type: {
			immediate: true,
			handler (newVal: RunnitNodeDefToolType) {
				if (
					newVal === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY &&
					this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
					this.team?.companyId
				) {
					this.companyIdsArray = _union(this.companyIdsArray, [this.team.companyId]);
				}
			}
		},
		onboardingData: {
			immediate: true,
			handler (newVal) {
				if (newVal?.enabledAt) {
					const { dateStr, timeInMinutes } = convertMomentToDateTimeInputValues(moment(newVal.enabledAt));
					this.onboardingEnabledAtDate = dateStr;
					this.onboardingEnabledAtTimeInMinutes = timeInMinutes;
				} else {
					this.onboardingEnabledAtDate = null;
					this.onboardingEnabledAtTimeInMinutes = null;
				}
			},
		},
	},
	computed: {
		...mapState([
			'team',
			'runnitState',
		]),
		allProviderMappingsValid (): boolean {
			return Array.isArray(this.newProviderMappings) &&
				this.newProviderMappings.length > 0 &&
				this.newProviderMappings.every((m: ProviderMapping) =>
					!!m?.provider && !!this._trim(m?.endpointId || ''));
		},
		isDisabled () {
			return this.isReadOnly ||
				this.savingRunnitNodeDef ||
				this.loadingPrivateNodeDef ||
				!this.newFields ||
				!this.privateNewFieldValues ||
				!this.allProviderMappingsValid ||
				this.isUploadingAvatar;
		},
	},
	methods: {
		tokensToHundredths,
		_isEmpty,
		_trim,
		hundredthsToTokens,
		...mapActions([
			'updateSnackbar',
		]),
		setOpen (val: boolean) {
			this.open = !!val;
			if (this.open !== this.value) {
				this.$emit('input', this.open);
			}
		},
		onCancel () {
			this.setOpen(false);
		},
		onNewFieldsInput (newFields: string) {
			this.newFields = newFields;
		},
		onPrivateNewFieldValuesInput (newFieldValues: string) {
			this.privateNewFieldValues = newFieldValues;
		},
		patchProviderMappingAt (index: number, patch: Partial<ProviderMapping>) {
			const current = this.newProviderMappings[index];
			if (!current) return;
			this.$set(this.newProviderMappings, index, {
				...current,
				...patch,
			} as ProviderMapping);
		},
		setProviderMappingJsonAt (kind: ProviderMappingJsonKind, index: number, value: string) {
			if (!this.newProviderMappings[index]) return;
			if (kind === 'fieldMappings') {
				this.$set(this.newProviderFieldMappingsJson, index, value);
			} else {
				this.$set(this.newProviderFieldMapBuildersJson, index, value);
			}
			try {
				const parsed = JSON.parse(value || '{}');
				this.patchProviderMappingAt(index, {
					[kind]: parsed,
				} as Partial<ProviderMapping>);
			} catch (e) {
				// Swallow parse error while user is typing invalid JSON.
			}
		},
		onRowFieldMappingsJson ({ index, value }: { index: number; value: string }) {
			this.setProviderMappingJsonAt('fieldMappings', index, value);
		},
		onRowFieldMapBuildersJson ({ index, value }: { index: number; value: string }) {
			this.setProviderMappingJsonAt('fieldMapBuilders', index, value);
		},
		onRowUpdateProvider ({ index, value }: { index: number; value: string | null }) {
			this.patchProviderMappingAt(index, {
				provider: value,
				endpointId: value ? '' : null,
			});
		},
		onRowUpdateEndpointId ({ index, value }: { index: number; value: string }) {
			this.patchProviderMappingAt(index, {
				endpointId: value || '',
			});
		},
		onRowUpdateNumResultsKey ({ index, value }: { index: number; value: string }) {
			const m = this.newProviderMappings[index];
			if (!m) return;
			const base = _cloneDeep(m.staticFieldMappings || {}) as Record<string, unknown>;
			const trimmed = this._trim(value || '');
			if (!trimmed) {
				delete base.numResults;
			} else {
				(base as { numResults?: { key: string; type: string } }).numResults = {
					key: trimmed,
					type: RUNNIT_NODE_FIELD_TYPE.NUM,
				};
			}
			this.patchProviderMappingAt(index, { staticFieldMappings: base });
		},
		resetProviderMappingJsonAt (kind: ProviderMappingJsonKind, index: number) {
			const oldEntry = this.oldProviderMappings[index];
			const entry = this.newProviderMappings[index];
			if (!entry) return;
			const oldJson = kind === 'fieldMappings'
				? this.oldProviderFieldMappingsJson[index]
				: this.oldProviderFieldMapBuildersJson[index];
			const fallbackJson = oldEntry
				? stringifyMappingObject(kind === 'fieldMappings' ? oldEntry.fieldMappings : oldEntry.fieldMapBuilders)
				: '{}';
			const json = oldJson ?? fallbackJson;
			if (kind === 'fieldMappings') {
				this.$set(this.newProviderFieldMappingsJson, index, json);
			} else {
				this.$set(this.newProviderFieldMapBuildersJson, index, json);
			}
			if (oldEntry) {
				this.patchProviderMappingAt(index, {
					[kind]: _cloneDeep(oldEntry[kind]),
				} as Partial<ProviderMapping>);
			}
		},
		onResetFieldMappingsAt (index: number) {
			this.resetProviderMappingJsonAt('fieldMappings', index);
		},
		onResetFieldMapBuildersAt (index: number) {
			this.resetProviderMappingJsonAt('fieldMapBuilders', index);
		},
		onProviderMappingRowFocus (index: number) {
			this.populateJsonTargetIndex = index;
		},
		swapProviderMappingRowsAt (i: number, j: number) {
			const swap = <T,> (arr: T[], a: number, b: number) => {
				const va = arr[a];
				const vb = arr[b];
				this.$set(arr, a, vb);
				this.$set(arr, b, va);
			};
			swap(this.newProviderMappings, i, j);
			swap(this.oldProviderMappings, i, j);
			swap(this.newProviderFieldMappingsJson, i, j);
			swap(this.newProviderFieldMapBuildersJson, i, j);
			swap(this.oldProviderFieldMappingsJson, i, j);
			swap(this.oldProviderFieldMapBuildersJson, i, j);
		},
		onProviderMappingMoveUp (index: number) {
			if (index <= 0 || index >= this.newProviderMappings.length) return;
			const prevFocus = this.populateJsonTargetIndex;
			this.swapProviderMappingRowsAt(index, index - 1);
			if (prevFocus === index) this.populateJsonTargetIndex = index - 1;
			else if (prevFocus === index - 1) this.populateJsonTargetIndex = index;
		},
		onProviderMappingMoveDown (index: number) {
			if (index < 0 || index >= this.newProviderMappings.length - 1) return;
			const prevFocus = this.populateJsonTargetIndex;
			this.swapProviderMappingRowsAt(index, index + 1);
			if (prevFocus === index) this.populateJsonTargetIndex = index + 1;
			else if (prevFocus === index + 1) this.populateJsonTargetIndex = index;
		},
		onAddProviderMapping () {
			const row = {
				...createEmptyProviderMapping(),
				provider: RUNNIT_NODE_DEF_PROVIDER.RUNNIT_IMG,
				endpointId: '',
			} as ProviderMapping;
			this.newProviderMappings.unshift(row);
			this.newProviderFieldMappingsJson.unshift('{}');
			this.newProviderFieldMapBuildersJson.unshift('{}');
			this.oldProviderMappings.unshift(null);
			this.oldProviderFieldMappingsJson.unshift('{}');
			this.oldProviderFieldMapBuildersJson.unshift('{}');
			this.populateJsonTargetIndex = 0;
		},
		onRemoveProviderMappingAt (index: number) {
			if (this.newProviderMappings.length <= 1) return;
			const prevFocus = this.populateJsonTargetIndex;
			this.newProviderMappings.splice(index, 1);
			this.oldProviderMappings.splice(index, 1);
			this.newProviderFieldMappingsJson.splice(index, 1);
			this.newProviderFieldMapBuildersJson.splice(index, 1);
			this.oldProviderFieldMappingsJson.splice(index, 1);
			this.oldProviderFieldMapBuildersJson.splice(index, 1);
			if (prevFocus === index) {
				this.populateJsonTargetIndex = Math.max(0, index - 1);
			} else if (prevFocus > index) {
				this.populateJsonTargetIndex = prevFocus - 1;
			}
			this.populateJsonTargetIndex = Math.min(this.populateJsonTargetIndex, this.newProviderMappings.length - 1);
		},
		buildStaticFieldMappingsForSavedRow (index: number): Record<string, unknown> {
			const m = this.newProviderMappings[index];
			const existing = _cloneDeep(m?.staticFieldMappings || {}) as Record<string, unknown>;
			if (this.canPickNumResults === this.CAN_PICK_NUM_RESULTS.N_A) {
				delete existing.numResults;
				return existing;
			}
			const key = this._trim(this._get(existing, 'numResults.key') || '');
			if (!key) {
				delete existing.numResults;
			} else {
				existing.numResults = {
					key,
					type: RUNNIT_NODE_FIELD_TYPE.NUM,
				};
			}
			return existing;
		},
		getActiveProviderMapping (providerMappings: ProviderMapping[], index: number): ProviderMapping | null {
			return providerMappings?.[index] || providerMappings?.[0] || null;
		},
		onNewCostPerResultLogicsInput (newLogics: string) {
			this.newCostPerResultLogics = newLogics;
		},
		checkFieldMappingsDisconnected (
			parsedNewFields: (RunnitNodeFieldGroup | RunnitNodeField)[],
			parsedNewFieldValues: RunnitPrivateNodeDefFieldValues,
			parsedProviderMappingRows: ParsedProviderMappingRow[],
		): string {
			const mappingRows: ParsedProviderMappingRow[] = parsedProviderMappingRows?.length
				? parsedProviderMappingRows
				: [{ fieldMappings: {}, fieldMapBuilders: {} }];

			const rowSatisfiesUuid = (
				row: ParsedProviderMappingRow,
				fieldDefUuid: FieldDefUuid,
			): boolean => {
				const fm: RunnitPrivateNodeDefFieldMappings = row.fieldMappings || {};
				const fmb: RunnitPrivateNodeDefFieldMapBuilders = row.fieldMapBuilders || {};
				if (!fm[fieldDefUuid]) return false;
				const builderKey: RunnitNodeDefFieldMapBuilderKey = this._get(fm[fieldDefUuid], 'builderKey');
				if (!builderKey) return true;
				const builder = fmb[builderKey];
				if (!builder) return false;
				if (builder.type === RUNNIT_NODE_DEF_FIELD_MAP_BUILDER_VALUE_TYPE.STR_MUSTACHE) {
					return typeof builder.template === 'string' && builder.template.includes(fieldDefUuid);
				}
				return true;
			};

			const fieldLogicReferences = new Set<FieldDefUuid>();
			(parsedNewFields || []).forEach((field: RunnitNodeFieldGroup | RunnitNodeField) => {
				const f = field as RunnitNodeFieldGroup & RunnitNodeField;
				_keys(f.logic?.field || {}).forEach((logicFieldDefUuid: FieldDefUuid) => {
					fieldLogicReferences.add(logicFieldDefUuid);
				});
			});
			const fieldValuesLogicReferences = new Set<FieldDefUuid>();
			Object.values(parsedNewFieldValues || {}).forEach((fv: { logic?: RunnitPrivateFieldValueLogic }) => {
				_keys(fv.logic?.field || {}).forEach((logicFieldDefUuid: FieldDefUuid) => {
					fieldValuesLogicReferences.add(logicFieldDefUuid);
				});
			});
			/** Same as before: logic-only refs do not require API fieldMappings on providers */
			const skipMappingBecauseLogicOnly = (fieldDefUuid: FieldDefUuid): boolean =>
				fieldLogicReferences.has(fieldDefUuid) || fieldValuesLogicReferences.has(fieldDefUuid);

			const fieldDefUuidsFromFields: FieldDefUuid[] = [];
			const fieldValueUuids = new Set<FieldDefUuid>(_keys(parsedNewFieldValues || {}) as FieldDefUuid[]);
			const collectFieldDefUuidsFromFields = (fields: (RunnitNodeField | RunnitNodeFieldGroup)[]): void => {
				(fields || []).forEach((groupOrField: RunnitNodeField | RunnitNodeFieldGroup) => {
					const group: RunnitNodeFieldGroup = groupOrField as RunnitNodeFieldGroup;
					const field: RunnitNodeField = groupOrField as RunnitNodeField;
					if (group.__rgroup) {
						collectFieldDefUuidsFromFields(group.fields);
					} else if (field.__rfield) {
						fieldDefUuidsFromFields.push(field.fieldDefUuid);
					}
				});
			};
			collectFieldDefUuidsFromFields(parsedNewFields);

			let unverifiedFieldDefUuid: FieldDefUuid = null;

			for (const fieldDefUuid of fieldDefUuidsFromFields) {
				// Field Values UUIDs are validated separately with "at least one mapping" semantics.
				if (fieldValueUuids.has(fieldDefUuid)) continue;
				if (skipMappingBecauseLogicOnly(fieldDefUuid)) continue;
				const everyRowHasMapping: boolean = mappingRows.every((row) => rowSatisfiesUuid(row, fieldDefUuid));
				if (!everyRowHasMapping) {
					unverifiedFieldDefUuid = fieldDefUuid;
					break;
				}
			}

			if (!unverifiedFieldDefUuid) {
				Array.from(fieldValueUuids).some((fieldDefUuid: FieldDefUuid) => {
					if (skipMappingBecauseLogicOnly(fieldDefUuid)) return false;
					const someRowHasMapping: boolean = mappingRows.some((row) => rowSatisfiesUuid(row, fieldDefUuid));
					if (!someRowHasMapping) {
						unverifiedFieldDefUuid = fieldDefUuid;
						return true;
					}
					return false;
				});
			}

			if (unverifiedFieldDefUuid) {
				return `The fieldDefUuid "${unverifiedFieldDefUuid}" is not found in "Field Mappings JSON" or is not correct in "Field Map Builders JSON"`;
			}

			return null;
		},
		handleKeydown (event: KeyboardEvent) {
			// Check for Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
			if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
				// Only trigger if the dialog is open and save button is not disabled
				if (this.open && !this.isDisabled) {
					event.preventDefault();
					this.onSaveClick();
				}
			}
		},
		async onSaveClick () {
			if (this.isReadOnly) return;
			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			/*
			JSON parsing error handling
			 */
			let parsedNewFields: (RunnitNodeFieldGroup | RunnitNodeField)[],
				parsedNewFieldValues: RunnitPrivateNodeDefFieldValues,
				parsedNewCostPerResultLogics: any[];
			let parsedProviderRows: ParsedProviderMappingRow[];
			try {
				parsedNewFields = JSON.parse(this.newFields || '{}');
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error parsing Fields JSON`,
					show: true,
				});
				return;
			}
			try {
				parsedNewFieldValues = JSON.parse(this.privateNewFieldValues || '{}');
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error parsing Field Values JSON`,
					show: true,
				});
				return;
			}
			parsedProviderRows = [];
			for (let i = 0; i < this.newProviderMappings.length; i++) {
				try {
					const fieldMappings = JSON.parse(this.newProviderFieldMappingsJson[i] || '{}');
					const fieldMapBuilders = JSON.parse(this.newProviderFieldMapBuildersJson[i] || '{}');
					parsedProviderRows.push({ fieldMappings, fieldMapBuilders });
				} catch (e) {
					console.error(e);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Error parsing Field Mappings / Map Builders JSON for provider mapping ${i + 1}`,
						show: true,
					});
					return;
				}
			}
			try {
				parsedNewCostPerResultLogics = JSON.parse(this.newCostPerResultLogics || '[]');
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error parsing Cost Per Result Logics JSON`,
					show: true,
				});
				return;
			}

			/*
			Fields syntax error handling
			 */
			const hasSyntaxErrorsInFields: Function = (fields: (RunnitNodeField | RunnitNodeFieldGroup)[]): boolean => (
				(fields || []).some((groupOrField: RunnitNodeField | RunnitNodeFieldGroup) => {
					const group: RunnitNodeFieldGroup = groupOrField as RunnitNodeFieldGroup;
					const field: RunnitNodeField = groupOrField as RunnitNodeField;
					return (
						!(group.__rgroup === true || field.__rfield === true) ||
						(group.__rgroup && hasSyntaxErrorsInFields(group.fields))
					);
				})
			);
			if (hasSyntaxErrorsInFields(parsedNewFields)) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Every object in "Fields JSON" must have \`"__rgroup": true\` or \`"__rfield": true\``,
					show: true,
				});
				return;
			}

			/*
			Field mappings error handling
			 */
			// Disconnect check: every Fields uuid must map on every provider row; every Field Values key must map on at least one row
			const fieldMappingsDisconnectedMessage: string = this.checkFieldMappingsDisconnected(
				parsedNewFields,
				parsedNewFieldValues,
				parsedProviderRows,
			);
			if (
				fieldMappingsDisconnectedMessage &&
				!confirm(`There is a disconnect with your field mappings. Message: ${fieldMappingsDisconnectedMessage}. Are you sure you want to save anyway?`)
			) {
				return;
			}
			const hasModelFamilyTag: boolean = (this.tags || []).some(
				(tag: RunnitNodeDefTag) => tag?.type === RUNNIT_NODE_DEF_TAG_TYPE.MODEL_FAMILY,
			);
			if (
				!hasModelFamilyTag &&
				!confirm('No tag selected for "Model Family". Are you sure you want to mark this tool as "Uncategorized" and save anyway? For example, if a customer is blocking ByteDance models out-right, and you incorrectly mark this as "Uncategorized", then this tool will not be blocked.')
			) {
				return;
			}

			// A published tool with no Media tag is invisible to the per-media token
			// caps on a team role: resolveTokenLimitMax finds nothing to match and
			// falls back to the role's master cap. A customer hit exactly that, unable
			// to run Kling 3.0 Pro while their Video cap sat well above its cost.
			//
			// Unlike the Model Family warning above this is a hard stop rather than a
			// confirm, because there is no case for publishing a chargeable tool with
			// no media type, and the failure is silent and customer-visible.
			const hasMediaTypeTag: boolean = (this.tags || []).some(
				(tag: RunnitNodeDefTag) => tag?.type === RUNNIT_NODE_DEF_TAG_TYPE.MEDIA,
			);
			if (!!this.publishedAt && !hasMediaTypeTag) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'This tool needs a Media tag (Image, Video, 3D, and so on) before it can be published. Without one, the per-media token limits on a team role cannot see it.',
					show: true,
				});
				return;
			}

			try {
				this.savingRunnitNodeDef = true;

				await db.runTransaction(async (transaction) => {
					const isCompanyNodeDef: boolean = this.type === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY;

					// update runnitNodeDef
					const staticFields: RunnitNodeStaticFields = this.canPickNumResults === this.CAN_PICK_NUM_RESULTS.YES ? {
						numResults: {
							defaultValue: this.defaultNumResults,
							max: this.maxNumResults,
							min: this.minNumResults,
						},
					} : {};

					const companyIds: Record<string, true> = isCompanyNodeDef ? this.companyIdsArray.reduce((map: Record<string, true>, companyId: string) => ({
						...map,
						[companyId]: true,
					}), {}) : null;
					const updatedNodeDef = {
						publishedAt: this.publishedAt,
						isPublished: !!this.publishedAt,
						title: this.title,
						description: this.description,
						articleUrl: this.articleUrl,
						imageUrl: this.imageUrl || null,
						avatarId: this.avatarId ? this.avatarId : null,
						avatar: this.avatarId ? db.doc(`avatars/${this.avatarId}`) : null,
						type: this.type,
						companyIds: !_isEmpty(companyIds) ? companyIds : FieldValue.delete(),
						sortOrder: this.sortOrder,
						averageGenInSeconds: this.averageGenInSeconds || null,
						costPerResult: Math.floor(this.tokensPerResult || 0),
						costPerResultLogics: parsedNewCostPerResultLogics,
						fields: parsedNewFields,
						staticFields,
						coldStartWarningSeconds: this.coldStartWarningSeconds || null,
						onboardingData: {
							descriptionHtml: this.onboardingData.descriptionHtml || null,
							videoUrl: this.onboardingData.videoUrl || null,
							enabledAt: this.onboardingData.enabledAt ? new Date(this.onboardingData.enabledAt) : null,
						},
						cmsData: {
							ghostPostIds: this.cmsData.ghostPostIds || {},
						},
						...this.getTagsSaveData(),
						showFeatured: this.showFeatured,
						newUntil: this.showNewTag ? new Date(this.newUntil) : null,
						usePromptDrivenForm: this.usePromptDrivenForm,
						batchable: this.batchable,
					};
					transaction.update(
						db.doc(`runnitNodeDefs/${this.selectedNodeDef.id}`),
						updatedNodeDef,
					);

					// update runnitPrivateNodeDef
					const staticFieldValues = this.canPickNumResults === this.CAN_PICK_NUM_RESULTS.NO && this.defaultNumResults > 1 ? {
						numResults: this.defaultNumResults,
					} : {};

					const nextProviderMappings: ProviderMapping[] = this.newProviderMappings.map((providerMapping: ProviderMapping, index: number) => {
						const pr = parsedProviderRows[index];
						return {
							...providerMapping,
							version: providerMapping?.version || '1.0.0',
							fieldMappings: pr.fieldMappings,
							fieldMapBuilders: pr.fieldMapBuilders,
							staticFieldMappings: this.buildStaticFieldMappingsForSavedRow(index),
						} as ProviderMapping;
					});
					transaction.update(
						db.doc(`runnitPrivateNodeDefs/${this.selectedNodeDef.id}`),
						{
							providerMappings: nextProviderMappings,
							fieldValues: parsedNewFieldValues,
							staticFieldValues,
						}
					)
				});

				// Normalize every mapping object and JSON buffer to the freshly parsed values
				for (let i = 0; i < this.newProviderMappings.length; i++) {
					const activeEntry = this.newProviderMappings[i];
					const pr = parsedProviderRows[i];
					if (activeEntry && pr) {
						this.$set(this.newProviderMappings, i, {
							...activeEntry,
							fieldMappings: pr.fieldMappings,
							fieldMapBuilders: pr.fieldMapBuilders,
						});
						this.$set(this.newProviderFieldMappingsJson, i, JSON.stringify(pr.fieldMappings, null, 2));
						this.$set(this.newProviderFieldMapBuildersJson, i, JSON.stringify(pr.fieldMapBuilders, null, 2));
					}
				}
				// Reset JSON state values
				this.oldFields = JSON.stringify(parsedNewFields, null, 2);
				this.newFields = this.oldFields;
				this.privateOldFieldValues = JSON.stringify(parsedNewFieldValues, null, 2);
				this.privateNewFieldValues = this.privateOldFieldValues;
				this.oldProviderMappings = _cloneDeep(this.newProviderMappings || []);
				this.oldProviderFieldMappingsJson = this.newProviderFieldMappingsJson.slice();
				this.oldProviderFieldMapBuildersJson = this.newProviderFieldMapBuildersJson.slice();
				this.oldCostPerResultLogics = JSON.stringify(parsedNewCostPerResultLogics, null, 2);
				this.newCostPerResultLogics = this.oldCostPerResultLogics;

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Success! Tool settings saved`,
					show: true,
				});
				this.onCancel();
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error! Problem with saving tool settings, please try again`,
					show: true,
				});
			} finally {
				this.savingRunnitNodeDef = false;
			}
		},
		async bindPrivateNodeDef (nodeDef: RunnitNodeDef) {
			try {
				this.loadingPrivateNodeDef = true;
				const privateNodeDefRef: DocumentReference = db.doc(`runnitPrivateNodeDefs/${nodeDef.id}`);
				await this.$bind(
					'privateNodeDef',
					privateNodeDefRef,
					get$bindFirestoreOptions(),
				);
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading privateNodeDef',
					show: true,
				});
			} finally {
				this.loadingPrivateNodeDef = false;
			}
		},
		async onDuplicate () {
			if (confirm('Are you sure you want to duplicate this tool?')) {
				try {
					this.savingRunnitNodeDef = true;

					// Reference the existing 'runnitNodeDefs' document
					const runnitNodeDefRef: DocumentReference = db.doc(`runnitNodeDefs/${this.selectedNodeDef.id}`);
					const runnitPrivateNodeDefRef: DocumentReference = db.doc(`runnitPrivateNodeDefs/${this.selectedNodeDef.id}`);

					/*
					GET DATA FOR DUPLICATING THE NODE DEF
					 */
					// Get the existing data from 'runnitNodeDefs'
					const docNodeDefSnapshot = await runnitNodeDefRef.get();
					if (!docNodeDefSnapshot.exists) {
						throw new Error("runnitNodeDefs document does not exist!");
					}
					const existingNodeDefData = docNodeDefSnapshot.data();

					// Modify the title to indicate it's a duplicated entry
					const duplicatedTitle = `${existingNodeDefData.title} Duplicated`;

					// Roll all the uuids in the NodeDef fields data to new uuids
					const oldToNewUuidMap: Record<string, string> = {};
					const rollUuidsInNodeFields: Function = (fields: (RunnitNodeField | RunnitNodeFieldGroup)[]): void => {
						(fields || []).forEach((groupOrField: RunnitNodeField | RunnitNodeFieldGroup) => {
							const group: RunnitNodeFieldGroup = groupOrField as RunnitNodeFieldGroup;
							const field: RunnitNodeField = groupOrField as RunnitNodeField;
							if (group.__rgroup) {
								rollUuidsInNodeFields(group.fields);
							} else if (field.__rfield) {
								if (!oldToNewUuidMap[field.uuid]) {
									oldToNewUuidMap[field.uuid] = uuidv4();
								}
								if (!oldToNewUuidMap[field.fieldDefUuid]) {
									oldToNewUuidMap[field.fieldDefUuid] = uuidv4();
								}
								field.uuid = oldToNewUuidMap[field.uuid];
								field.fieldDefUuid = oldToNewUuidMap[field.fieldDefUuid];
							}
						});
					};
					rollUuidsInNodeFields(existingNodeDefData.fields);

					// Roll all the fieldDefUuids in the NodeDef fields logic data to the newly generated fieldDefUuids
					const rollFieldDefUuidsInNodeFieldLogics: Function = (fields: (RunnitNodeField | RunnitNodeFieldGroup)[]): void => {
						(fields || []).forEach((groupOrField: RunnitNodeField | RunnitNodeFieldGroup) => {
							const group: RunnitNodeFieldGroup = groupOrField as RunnitNodeFieldGroup;
							const field: RunnitNodeField = groupOrField as RunnitNodeField;
							if (group.__rgroup) {
								rollFieldDefUuidsInNodeFieldLogics(group.fields);
								if (group.logic) {
									const clonedLogic: RunnitNodeFieldLogic = _cloneDeep(group.logic);
									group.logic.field = {};
									_keys(clonedLogic.field || {}).forEach((oldLogicFieldDefUuid: FieldDefUuid) => {
										const newUuid: FieldDefUuid = oldToNewUuidMap[oldLogicFieldDefUuid];
										if (!newUuid) return; // skip orphaned field refs — avoids writing a literal "undefined" key
										group.logic.field[newUuid] = clonedLogic.field[oldLogicFieldDefUuid] || {};
									});
								}
							} else if (field.__rfield) {
								if (field.logic) {
									const clonedLogic: RunnitNodeFieldLogic = _cloneDeep(field.logic);
									field.logic.field = {};
									_keys(clonedLogic.field || {}).forEach((oldLogicFieldDefUuid: FieldDefUuid) => {
										const newUuid: FieldDefUuid = oldToNewUuidMap[oldLogicFieldDefUuid];
										if (!newUuid) return; // skip orphaned field refs — avoids writing a literal "undefined" key
										field.logic.field[newUuid] = clonedLogic.field[oldLogicFieldDefUuid] || {};
									});
								}
							}
						});
					};
					rollFieldDefUuidsInNodeFieldLogics(existingNodeDefData.fields);

					// Roll all the fieldDefUuids in the costPerResultLogics
					const rollFieldDefUuidsInCostPerResultLogics = (logics: CostPerResultLogic[]): void => {
						(logics || []).forEach((logic) => {
							const clonedLogic = _cloneDeep(logic);
							logic.field = {};
							_keys(clonedLogic.field || {}).forEach((oldLogicFieldDefUuid: FieldDefUuid) => {
								const newUuid: FieldDefUuid = oldToNewUuidMap[oldLogicFieldDefUuid];
								if (!newUuid) return; // skip orphaned field refs — avoids writing a literal "undefined" key
								logic.field[newUuid] = clonedLogic.field[oldLogicFieldDefUuid];
							});
						});
					};
					rollFieldDefUuidsInCostPerResultLogics(existingNodeDefData.costPerResultLogics);

					/*
					GET DATA FOR DUPLICATING THE PRIVATE NODE DEF
					 */
					// Get the existing data from 'runnitPrivateNodeDefs'
					const docPrivateNodeDefSnapshot: DocumentSnapshot = await runnitPrivateNodeDefRef.get();
					if (!docPrivateNodeDefSnapshot.exists) {
						throw new Error("runnitPrivateNodeDefs document does not exist!");
					}
					const existingPrivateNodeDefData = docPrivateNodeDefSnapshot.data() as RunnitPrivateNodeDef;

					// Roll all the uuids in the PrivateNodeDef field mappings and values data to new uuids
					const providerMappings: ProviderMapping[] = _cloneDeep(existingPrivateNodeDefData?.providerMappings || []);

					// Roll fieldMappings UUID keys for every provider mapping
					providerMappings.forEach((pm: ProviderMapping) => {
						const oldFm: RunnitPrivateNodeDefFieldMappings = _cloneDeep(pm.fieldMappings || {});
						pm.fieldMappings = {};
						_keys(oldFm).forEach((fieldDefUuid: FieldDefUuid) => {
							if (!oldToNewUuidMap[fieldDefUuid]) {
								oldToNewUuidMap[fieldDefUuid] = uuidv4();
							}

							const oldMappingValue = oldFm[fieldDefUuid];
							if (!oldMappingValue) return;
							pm.fieldMappings[oldToNewUuidMap[fieldDefUuid]] = _cloneDeep(oldMappingValue);
						});
					});

					// Roll old fieldDefUuid values embedded in STR_MUSTACHE fieldMapBuilders templates for every mapping
					// (oldToNewUuidMap is fully populated after the fieldMappings roll above)
					providerMappings.forEach((pm: ProviderMapping) => {
						_keys(pm.fieldMapBuilders || {}).forEach((builderKey: RunnitNodeDefFieldMapBuilderKey) => {
							const builder = pm.fieldMapBuilders[builderKey];
							if (builder?.type === RUNNIT_NODE_DEF_FIELD_MAP_BUILDER_VALUE_TYPE.STR_MUSTACHE && builder.template) {
								builder.template = _keys(oldToNewUuidMap).reduce(
									(tpl: string, oldUuid: string) => tpl.split(oldUuid).join(oldToNewUuidMap[oldUuid]),
									builder.template,
								);
							}
						});
					});

					// Roll fieldValues UUID keys into a fresh object (canonical private def shape only on write)
					const oldFieldValues: RunnitPrivateNodeDefFieldValues = existingPrivateNodeDefData.fieldValues || {};
					const nextFieldValues: RunnitPrivateNodeDefFieldValues = {};
					_keys(oldFieldValues).forEach((fieldDefUuid: FieldDefUuid) => {
						if (!oldToNewUuidMap[fieldDefUuid]) {
							oldToNewUuidMap[fieldDefUuid] = uuidv4();
						}
						nextFieldValues[oldToNewUuidMap[fieldDefUuid]] = _cloneDeep(oldFieldValues[fieldDefUuid]);
						const field = nextFieldValues[oldToNewUuidMap[fieldDefUuid]];
						if (field.logic) {
							const clonedLogic: RunnitNodeFieldLogic = _cloneDeep(field.logic);
							field.logic.field = {};
							_keys(clonedLogic.field || {}).forEach((oldLogicFieldDefUuid: FieldDefUuid) => {
								field.logic.field[oldToNewUuidMap[oldLogicFieldDefUuid]] = clonedLogic.field[oldLogicFieldDefUuid] || {};
							});
						}
					});
					const nextStaticFieldValues = _cloneDeep(existingPrivateNodeDefData.staticFieldValues || {});

					await db.runTransaction(async (transaction) => {
						const isCompanyNodeDef: boolean = !!(
							existingNodeDefData.type === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY &&
							this.team &&
							this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM
						);

						// Create a new document in 'runnitNodeDefs' with modified title
						const newRunnitNodeDefRef = db.collection('runnitNodeDefs').doc();
						transaction.set(newRunnitNodeDefRef, {
							...existingNodeDefData,
							createdAt: new Date(),
							publishedAt: null,
							isPublished: false,
							deletedAt: null,
							isDeleted: false,
							title: duplicatedTitle,  // Update the title in the new document
							...(isCompanyNodeDef && {
								type: RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY,
								companyIds: { [this.team.companyId]: true },
							}),
							...(!isCompanyNodeDef && existingNodeDefData.type === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY && {
								type: RUNNIT_NODE_DEF_TOOL_TYPE.CORE,
							}),
							tags: null,
							tagTypes: null,
							costPerResultLogics: existingNodeDefData.costPerResultLogics || [],
							newUntil: existingNodeDefData.newUntil ? (existingNodeDefData.newUntil.toDate ? existingNodeDefData.newUntil.toDate() : existingNodeDefData.newUntil) : null,
							onboardingData: {
								descriptionHtml: existingNodeDefData.onboardingData?.descriptionHtml || null,
								videoUrl: existingNodeDefData.onboardingData?.videoUrl || null,
								enabledAt: existingNodeDefData.onboardingData?.enabledAt
									? (existingNodeDefData.onboardingData.enabledAt.toDate ? existingNodeDefData.onboardingData.enabledAt.toDate() : existingNodeDefData.onboardingData.enabledAt)
									: null,
							},
						});

						// Use the same data to create a new document in 'runnitPrivateNodeDefs'
						transaction.set(
							db.doc(`runnitPrivateNodeDefs/${newRunnitNodeDefRef.id}`),
							{
								createdAt: new Date(),
								providerMappings,
								fieldValues: nextFieldValues,
								staticFieldValues: nextStaticFieldValues,
							},
						);
					});

					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Success! Duplicate tool created`,
						show: true,
					});
					this.onCancel();
				} catch (e) {
					console.error(e);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Error! Problem with duplicating tool, please try again`,
						show: true,
					});
				} finally {
					this.savingRunnitNodeDef = false;
				}
			}
		},
		async onDelete () {
			if (this.readOnly) return;
			if (confirm('Are you sure you want to delete this tool?')) {
				try {
					this.savingRunnitNodeDef = true;

					// Reference the existing 'runnitNodeDefs' document
					const runnitNodeDefRef: DocumentReference = db.doc(`runnitNodeDefs/${this.selectedNodeDef.id}`);

					// Soft Delete the document from 'runnitNodeDefs'
					await runnitNodeDefRef.update({
						deletedAt: new Date(),
						isDeleted: true,
					});

					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Success! Tool deleted`,
						show: true,
					});
					this.onCancel();
				} catch (e) {
					console.error(e);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Error! Problem with deleting tool settings, please try again`,
						show: true,
					});
				} finally {
					this.savingRunnitNodeDef = false;
				}
			}
		},
		onPublishedAtChange (selected: boolean) {
			if (this.publishedAt) {
				if (confirm('Are you sure you want to unpublish this tool?')) {
					this.publishedAtSelected = selected;
					this.publishedAt = selected ? new Date() : null;
				}
			} else {
				this.publishedAtSelected = selected;
				this.publishedAt = selected ? new Date() : null;
			}
		},
		async fetchCompanies () {
			try {
				this.fetchingCompanies = true;
				const companiesRef: Query = db.collection(`companies`)
					.orderBy('name', 'asc')
					.limit(3000);

				(await companiesRef.get()).forEach(async (doc: DocumentSnapshot) => {
					const company: Company = {
						...doc.data(),
						get id () { return doc.id },
					} as Company;
					this.companies.push(company);
				});
			} catch (err) {
				console.error(err);
			} finally {
				this.fetchingCompanies = false;
			}
		},
		handleAvatarUpload (avatar: Avatar) {
			if (this.isReadOnly) return;
			this.avatarId = avatar.id;
			this.avatar = avatar;
		},
		removeTag (tag) {
			if (this.isReadOnly) return;
			this.tags = this.tags.filter(t => t.id !== tag.id);
		},
		setTagSelection (tag) {
			this.tags = _unionBy(this.tags, [tag], 'id');
		},
		removeTagSelection (tag) {
			this.tags = this.tags.filter(({ id }) => id != tag.id);
		},
		onTagEdit (tag) {
			const tagIndex = this.tags.findIndex(t => t.id === tag.id)
			if (tagIndex !== -1) {
				if (tag.isDeleted) {
					this.tags.splice(tagIndex, 1);
				} else {
					this.tags.splice(tagIndex, 1, tag);
				}
			}
		},
		getTagsSaveData (): {
			tagTypes: Record<RunnitNodeDefTagType, true>
			tags: Record<string, true | RunnitNodeDefTagConnection>
		} {
			// Preserve any existing connection value (`true` or { sortOrder, … })
			// for a tag that's still selected, so saving the tool here never
			// clobbers a curated order or future per-connection metadata; a
			// newly-added tag joins as `true` (unordered). See ADR 0014.
			const existingTags: Record<string, any> = (this.selectedNodeDef && this.selectedNodeDef.tags) || {};
			return this.tags.reduce((saveData, tag) => {
				if (!saveData.tagTypes[tag.type]) {
					saveData.tagTypes[tag.type] = true;
				}
				saveData.tags[tag.id] = (tag.id in existingTags) ? existingTags[tag.id] : true;
				return saveData;
			}, { tagTypes: {}, tags: {} } as { tagTypes: Record<RunnitNodeDefTagType, true>; tags: Record<string, true | RunnitNodeDefTagConnection> });
		},
		onDollarsOurCostPerResultInput (cost: number) {
			this.dollarsOurCostPerResult = cost;
			this.setDollarsOurCostToTokens(cost);
		},
		setDollarsOurCostToTokens (cost: number) {
			// Get the tokens as displayed in the UI
			const costWithMargin: number = new BigNumber(cost)
				.times(this.INTERNAL_PRICING_RUNNIT_TOKENS_MARGIN_MULTIPLE)
				.toNumber();
			const tokens: number = Math.round(
				new BigNumber(costWithMargin)
					.div(this.INTERNAL_PRICING_RUNNIT_TOKENS_CONVERSION_MULTIPLE)
					.toNumber(),
			);
			// Convert to the tokens as stored in the DB
			this.tokensPerResult = tokensToHundredths(tokens);
		},
		onTokensPerResultInput (tokens: number) {
			this.tokensPerResult = tokensToHundredths(tokens);
			this.setTokensToDollars(tokens);
		},
		setTokensToDollars (tokens: number) {
			const costWithMargin: number = new BigNumber(tokens)
				.times(this.INTERNAL_PRICING_RUNNIT_TOKENS_CONVERSION_MULTIPLE)
				.toNumber();
			const cost: number = new BigNumber(costWithMargin)
				.div(this.INTERNAL_PRICING_RUNNIT_TOKENS_MARGIN_MULTIPLE)
				.toNumber();
			this.dollarsOurCostPerResult = cost;
		},
		copyRouteToLink () {
			const route = this.$router.resolve({
				name: ROUTER.RUNNIT_REROUTE,
				query: {
					toolId: this.selectedNodeDef.id,
				},
			});
			navigator.clipboard.writeText(`${window.location.origin}${route.href}`);
			this.updateSnackbar({
				status: SNACKBAR_STATUS.SUCCESS,
				message: `Success! Link copied to clipboard`,
				show: true,
			});
		},
		setOnboardingEnabledAtDate (dateStr) {
			this.onboardingEnabledAtDate = dateStr;

			// If date is set but no time is set, default to midnight (0 minutes)
			if (dateStr && this.onboardingEnabledAtTimeInMinutes === null) {
				this.onboardingEnabledAtTimeInMinutes = 0;
			}

			this.updateOnboardingEnabledAt();
		},
		setOnboardingEnabledAtTimeInMinutes (timeInMinutes) {
			this.onboardingEnabledAtTimeInMinutes = timeInMinutes;
			this.updateOnboardingEnabledAt();
		},
		addGhostPostId () {
			if (!this.newGhostPostId || this.isReadOnly || this.savingRunnitNodeDef) return;
			const trimmedId = _trim(this.newGhostPostId);
			if (!trimmedId) return;

			// Check if already exists
			if (this.ghostPostIdsArray.includes(trimmedId)) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Ghost Post ID already exists',
					show: true,
				});
				return;
			}

			this.ghostPostIdsArray.push(trimmedId);
			this.cmsData.ghostPostIds = this.ghostPostIdsArray.reduce((acc, id) => {
				acc[id] = true;
				return acc;
			}, {} as Record<string, true>);
			this.newGhostPostId = '';
		},
		removeGhostPostId (index: number) {
			if (this.isReadOnly || this.savingRunnitNodeDef) return;
			this.ghostPostIdsArray.splice(index, 1);
			this.cmsData.ghostPostIds = this.ghostPostIdsArray.reduce((acc, id) => {
				acc[id] = true;
				return acc;
			}, {} as Record<string, true>);
		},
		async syncCmsData () {
			if (!this.id || this.isReadOnly || this.savingRunnitNodeDef || this.syncingCms) return;
			if (this.ghostPostIdsArray.length === 0) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'No Ghost Post IDs configured',
					show: true,
				});
				return;
			}

			try {
				this.syncingCms = true;
				const syncNodeDefToCms = functions.httpsCallable('syncNodeDefToCms');
				const result = await syncNodeDefToCms({ nodeDefId: this.id });

				if (result.data?.success) {
					const syncedCms = result.data.syncedCms || [];
					const featuredCount = result.data.featuredNodeRunCount || 0;
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Successfully synced to ${syncedCms.join(', ')}. ${featuredCount} featured nodeRun(s) synced.`,
						show: true,
					});
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: result.data?.message || 'Failed to sync to CMS',
						show: true,
					});
				}
			} catch (error: any) {
				console.error('Error syncing to CMS:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: error.message || 'Error syncing to CMS',
					show: true,
				});
			} finally {
				this.syncingCms = false;
			}
		},
		updateOnboardingEnabledAt () {
			if (this.onboardingEnabledAtDate) {
				// Use the time if set, otherwise default to midnight (0 minutes)
				const timeToUse = this.onboardingEnabledAtTimeInMinutes !== null ? this.onboardingEnabledAtTimeInMinutes : 0;
				const combinedDateTime = combineDateAndTime(this.onboardingEnabledAtDate, timeToUse);

				this.onboardingData = {
					...this.onboardingData,
					enabledAt: combinedDateTime ? combinedDateTime.toISOString() : null,
				};
			} else {
				// No date set, clear everything
				this.onboardingData = {
					...this.onboardingData,
					enabledAt: null,
				};
				this.onboardingEnabledAtTimeInMinutes = null;
			}
		},
		async handlePopulateJsonConfirm (done: Function) {
			if (!this.populateJsonType || !this.populateJsonText) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Please fill in all required fields',
					show: true,
				});
				return;
			}
			this.populateJsonLoading = true;
			try {
				// populateJsonTargetIndex: last provider row that received focus (field mapping / map builder textareas)
				const generateToolJsons = functions.httpsCallable('generateToolJsons');
				const pjIdx = Math.min(this.populateJsonTargetIndex, (this.oldProviderFieldMappingsJson?.length || 1) - 1);
				const result = await generateToolJsons({
					toolType: this.populateJsonType,
					requestInterface: this.populateJsonText,
					currentFields: this.oldFields || undefined,
					currentFieldValues: this.privateOldFieldValues || undefined,
					currentFieldMappings: this.oldProviderFieldMappingsJson[pjIdx] || undefined,
				});
				console.log('generateToolJsons result', result);

				if (result?.data?.success) {
					const { fields, fieldValues, fieldMappings } = result.data.data;
					this.newFields = JSON.stringify(fields, null, 2);
					this.privateNewFieldValues = JSON.stringify(fieldValues, null, 2);
					const aiIdx = Math.min(this.populateJsonTargetIndex, this.newProviderMappings.length - 1);
					const aiEntry = this.newProviderMappings[aiIdx];
					if (aiEntry) {
						this.$set(this.newProviderMappings, aiIdx, { ...aiEntry, fieldMappings });
						this.$set(this.newProviderFieldMappingsJson, aiIdx, JSON.stringify(fieldMappings, null, 2));
					}

					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: 'JSONs populated successfully',
						show: true,
					});
				} else {
					const errorMessage = result?.data?.error || 'Unknown error occurred';
					throw new Error(errorMessage);
				}
			} catch (e: any) {
				console.error('Error populating JSONs:', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error populating JSONs: ${e.message}`,
					show: true,
				});
			} finally {
				this.populateJsonLoading = false;
				done();
			}
		},
	},
	components: {
		GreyButton,
		ClipboardCopy,
		ActionsIsland,
		NumberField,
		PurpleChip,
		DialogContent,
		ImageUpload,
		RunnitImage,
		ManageTagsDialog,
		BaseButton,
		TokensSVG,
		FeaturedNodeDefResultsContainer,
		DatePicker,
		TimePicker,
		HtmlField,
		ConfirmDialog,
		LoadingState,
		RunnitProviderMappingRow,
	},
});
